import { Box, Divider, Modal, Typography } from "@mui/material";
import {
  useCreatePartProcessV2,
  useUpdatePartProcessV2,
  useDeletePartProcessV2,
  useGetBreakdownProcessV1,
  useGetBreakdownProcessV2,
} from "@/hooks/breakdownHooks";
import CloseButton from "@/components/buttons/CloseButton";
import { InformationModal } from "@/styles/globalstyles";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import ProcessList from "./ProcessList";
import EditableProcessList from "./EditableProcessList";
import { useState } from "react";
import {
  useGetOEMPartProcesses,
  useGetSparePartProcesses,
} from "@/hooks/masterHook";
import { type Master } from "@/api/masterService";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";

interface ManagePartProcessModalProps {
  open: boolean;
  handleClose: () => void;
  supplierCode?: string;
  partId?: number;
  version: "v1" | "v2";
  canEdit: boolean;
}

const ManagePartProcessModal = ({
  open,
  handleClose,
  supplierCode,
  partId,
  version,
  canEdit,
}: ManagePartProcessModalProps) => {
  const [disabled, setDisabled] = useState(false);
  const {
    data: breakdownProcessV1,
    isLoading: isLoadingBreakdownProcessV1,
    isError: isErrorBreakdownProcessV1,
  } = useGetBreakdownProcessV1(supplierCode || "");

  const {
    data: breakdownProcessV2,
    isLoading: isLoadingBreakdownProcessV2,
    isError: isErrorBreakdownProcessV2,
  } = useGetBreakdownProcessV2(partId || 0);

  const {
    data: OEMPartProcesses,
    isLoading: isLoadingOEMPartProcesses,
    isError: isErrorOEMPartProcesses,
  } = useGetOEMPartProcesses({ skip: 0, limit: 1000, active_only: true }, true);

  const {
    data: sparePartProcesses,
    isLoading: isLoadingSparePartProcesses,
    isError: isErrorSparePartProcesses,
  } = useGetSparePartProcesses(
    { skip: 0, limit: 1000, active_only: true },
    true,
  );

  const { mutate: createPartProcessV2, isPending: isCreatingPartProcessV2 } =
    useCreatePartProcessV2();

  const { mutate: updatePartProcessV2, isPending: isUpdatingPartProcessV2 } =
    useUpdatePartProcessV2();

  const { mutate: deletePartProcessV2, isPending: isDeletingPartProcessV2 } =
    useDeletePartProcessV2();

  useQueryHandler({
    isFetching:
      isLoadingBreakdownProcessV1 ||
      isLoadingBreakdownProcessV2 ||
      isLoadingOEMPartProcesses ||
      isLoadingSparePartProcesses ||
      isCreatingPartProcessV2 ||
      isUpdatingPartProcessV2 ||
      isDeletingPartProcessV2,
    isError:
      isErrorBreakdownProcessV1 ||
      isErrorBreakdownProcessV2 ||
      isErrorOEMPartProcesses ||
      isErrorSparePartProcesses,
  });

  const onClose = () => {
    handleClose();
    setDisabled(false);
  };

  const handleAddOEMProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createPartProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_processes: [
            {
              ms_oem_process_v2_id: process.master_process_id,
            },
          ],
          spare_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error creating OEM part process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleAddSpareProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createPartProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_processes: [],
          spare_processes: [
            {
              ms_spare_process_v2_id: process.master_process_id,
            },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error creating spare part process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditOEMProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updatePartProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_processes: [
            {
              part_oem_process_id: process.id,
              ms_oem_process_v2_id: process.master_process_id,
            },
          ],
          spare_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error updating OEM process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditSpareProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updatePartProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_processes: [],
          spare_processes: [
            {
              part_spare_process_id: process.id,
              ms_spare_process_v2_id: process.master_process_id,
            },
          ],
        },
      },
      {
        onError: (error) => {
          console.error("Error updating spare process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteOEMProcess = async (processId: number) => {
    deletePartProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_processes: [{ part_oem_process_id: processId }],
          spare_processes: [],
        },
      },
      {
        onError: (error) => {
          console.error("Error deleting oem process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteSpareProcess = async (processId: number) => {
    deletePartProcessV2(
      {
        partId: partId || 0,
        params: {
          oem_processes: [],
          spare_processes: [{ part_spare_process_id: processId }],
        },
      },
      {
        onError: (error) => {
          console.error("Error deleting spare process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...InformationModal,
          minHeight: "400px",
          maxHeight: "90vh",
          width: "70vw",
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="h2">{"จัดการ Part Process"}</Typography>
        <Divider sx={{ my: 1 }} />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} p={1} pt={2}>
          {version === "v1" && (
            <>
              <ProcessList
                title="OEM Process"
                data={
                  breakdownProcessV1?.oem_process?.map((process) => ({
                    id: String(process.id),
                    name: process.name,
                  })) || []
                }
              />
              <ProcessList
                title="Spare Process"
                data={
                  breakdownProcessV1?.spare_process?.map((process) => ({
                    id: String(process.id),
                    name: process.name,
                  })) || []
                }
              />
            </>
          )}
          {version === "v2" && (
            <>
              <EditableProcessList
                title="OEM Part Process"
                data={
                  breakdownProcessV2?.oem_process?.map((process) => ({
                    id: process.id,
                    name: process.name,
                    master_process_id: process.master_process_id || 0,
                  })) || []
                }
                options={
                  OEMPartProcesses?.map((process: Master) => ({
                    value: process.id,
                    label: process.name,
                  })) || []
                }
                handleAddProcess={async (process) => {
                  handleAddOEMProcess(process);
                }}
                handleEditProcess={async (process) => {
                  handleEditOEMProcess(process);
                }}
                handleDeleteProcess={async (processId) => {
                  handleDeleteOEMProcess(processId);
                }}
                disabled={disabled || !canEdit}
                setDisabled={setDisabled}
              />
              <EditableProcessList
                title="Spare Part Process"
                data={
                  breakdownProcessV2?.spare_process?.map((process) => ({
                    id: process.id,
                    name: process.name,
                    master_process_id: process.master_process_id || 0,
                  })) || []
                }
                options={
                  sparePartProcesses?.map((process: Master) => ({
                    value: process.id,
                    label: process.name,
                  })) || []
                }
                handleAddProcess={async (process) => {
                  handleAddSpareProcess(process);
                }}
                handleEditProcess={async (process) => {
                  handleEditSpareProcess(process);
                }}
                handleDeleteProcess={async (processId) => {
                  handleDeleteSpareProcess(processId);
                }}
                disabled={disabled || !canEdit}
                setDisabled={setDisabled}
              />
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ManagePartProcessModal;
